import {
  Card,
  CardContent,
  Button,
  CryptoIcon,
  TabsHeader,
  TabsHeaderTitle,
  TabsHeaderActions,
} from '@valiant/ui-shared'
import { Copy, RefreshCw } from 'lucide-react'
import { useGetGasWalletsQuery, useRefreshGasWalletsMutation } from '@/generated/graphql'
import { toast } from 'sonner'

export function GasTab() {
  const [{ data: gasWalletsData, fetching: gasWalletsLoading }, executeGetGasWallets] =
    useGetGasWalletsQuery()
  const [{ fetching: refreshing }, executeRefreshGasWallets] = useRefreshGasWalletsMutation()

  const gasWallets = gasWalletsData?.getGasWallets || []

  const handleRefresh = async () => {
    await executeRefreshGasWallets({})
    // Refetch the gas wallets after refreshing
    executeGetGasWallets({ requestPolicy: 'network-only' })
    toast.success('Gas wallets refreshed successfully')
  }

  const handleCopyAddress = (address: string) => {
    navigator.clipboard.writeText(address)
    toast.success('Address copied to clipboard!')
  }

  const Label = ({ children }: { children: React.ReactNode }) => {
    return <div className="text-xs rounded-md w-fit text-card-text">{children}</div>
  }

  return (
    <div className="space-y-4">
      <TabsHeader className="flex-row justify-between">
        <TabsHeaderTitle>Gas Wallets</TabsHeaderTitle>
        <TabsHeaderActions>
          <Button
            onClick={handleRefresh}
            disabled={refreshing || gasWalletsLoading}
            variant="main_outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </TabsHeaderActions>
      </TabsHeader>

      <div className="space-y-4">
        {gasWallets.map((wallet, index) => (
          <Card key={index} className="border-0 shadow-sm max-w-[475px] py-0">
            <CardContent className="p-6">
              <div className="flex flex-col justify-between">
                <div className="flex items-center justify-between gap-4 w-full">
                  <div className="flex items-center gap-2">
                    <CryptoIcon symbol={wallet.type} size="h-8 w-8" />
                    <h3 className="font-semibold text-lg">{wallet.type}</h3>
                  </div>
                  <div className="text-right">
                    <Label>Balance</Label>
                    <p className="font-semibold">{wallet.balance}</p>
                    <p className="text-xs text-card-text">${wallet.usdValue}</p>
                  </div>
                </div>

                <div className="flex flex-col gap-2 mt-4">
                  <Label>Address</Label>
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-semibold break-all mr-4">{wallet.address}</p>
                    <Button
                      onClick={() => handleCopyAddress(wallet.address)}
                      variant="ghost"
                      size="sm"
                      className="h-auto md:h-auto text-xs text-card-text border border-card-border rounded-md px-2 py-1.5 has-[>svg]:px-2.5 w-fit cursor-pointer"
                    >
                      <Copy className="size-3" />
                      <span className="text-xs leading-none">Copy</span>
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
